import { useCallback } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '../redux/store';
import { selectMealsForDateRange } from '../redux/mealPlanSlice';
import { isRecipeMeal } from '../redux/mealEntry';
import { addDays } from '../utils/localDate';
import { primaryMealsOf } from '../utils/slotPrimary';
import { SAME_MEAL_LOOKBACK_DAYS, sameMealExcluder, type IdentifiableRecipe } from '../services/mealIdentity';

/**
 * The same-meal rules for a suggestion list (#736): given the day a slot falls
 * on, a predicate that is true for any recipe repeating a dinner planned within
 * two weeks of it. Reads the store when called, not on every render — the
 * suggestion lists only need it while a sheet is open.
 */
export function useSameMealExclude(): (date: string, ignoreMealId?: string) => (r: IdentifiableRecipe) => boolean {
  const store = useStore<RootState>();
  return useCallback((date: string, ignoreMealId?: string) => {
    const around = selectMealsForDateRange(
      store.getState(),
      addDays(date, -SAME_MEAL_LOOKBACK_DAYS),
      addDays(date, SAME_MEAL_LOOKBACK_DAYS),
    ).filter(isRecipeMeal);
    // The main of each slot only: a salad served beside a roast is not a
    // salad dinner, and must not keep salads off the next seven days (#746).
    return sameMealExcluder(primaryMealsOf(around), date, ignoreMealId);
  }, [store]);
}
